/* eslint-disable no-empty-pattern */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

export const getQuestionDropdowns = createAsyncThunk(
  "questionBank/getQuestionDropdowns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/v1/question/dropdowns`);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getQuestions = createAsyncThunk(
  "questionBank/getQuestions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await axios.get(`/api/admin/v1/question?${query}`);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getPredefinedThreads = createAsyncThunk(
  "questionBank/getPredefinedThreads",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/v1/question/predefined-threads`, { params });
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createPredefinedThread = createAsyncThunk(
  "questionBank/createPredefinedThread",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/admin/v1/question/predefined-thread`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || error);
    }
  },
);

export const deletePredefinedThread = createAsyncThunk(
  "questionBank/deletePredefinedThread",
  async (predefinedThreadId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/api/admin/v1/question/predefined-thread/${predefinedThreadId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || error);
    }
  },
);

export const deleteQuestionFromPredefinedThread = createAsyncThunk(
  "questionBank/deleteQuestionFromPredefinedThread",
  async ({ predefinedThreadId, questionId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/api/admin/v1/question/predefined-thread/${predefinedThreadId}/question/${questionId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || error);
    }
  },
);

export const getQuestionnaireThreads = createAsyncThunk(
  "questionBank/getQuestionnaireThreads",
  async ({ orgId, dealId, dealCategoryId }, { rejectWithValue }) => {
    try {
      let url = "api/admin/questionnaire-threads";

      const queryParams = [`orgId=${orgId}`];

      if (dealId) {
        queryParams.push(`dealId=${dealId}`);
      }

      if (dealCategoryId) {
        queryParams.push(`dealCategoryId=${dealCategoryId}`);
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }

      const response = await axios.get(url);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getIdQuestionnaireThread = createAsyncThunk(
  "questionBank/getIdQuestionnaireThread",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/questionnaire-thread/${id}`);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteQuestionnaireThread = createAsyncThunk(
  "questionBank/deleteQuestionnaireThread",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `/api/admin/questionnaire-thread/${id}`,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteThreadQuestion = createAsyncThunk(
  "questionBank/deleteThreadQuestion",
  async ({ questionId, threadId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `/api/admin/questionnaire-thread/question?questionId=${questionId}&threadId=${threadId}`,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getFilteredQuestions = createAsyncThunk(
  "questionBank/getFilteredQuestions",
  async (
    { categoryId, entityTypeId, dealCategoryId, orgId, dealId } = {},
    { rejectWithValue },
  ) => {
    try {
      let url = "/api/admin/v1/question/filter";

      const queryParams = [`orgId=${orgId}`];

      if (categoryId) {
        queryParams.push(`categoryId=${categoryId}`);
      }

      if (entityTypeId) {
        queryParams.push(`entityTypeId=${entityTypeId}`);
      }

      if (dealCategoryId) {
        queryParams.push(`dealCategoryId=${dealCategoryId}`);
      }

      if (dealId) {
        queryParams.push(`dealId=${dealId}`);
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }

      const response = await axios.get(url);

      return response.response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getMappedQuestions = createAsyncThunk(
  "questionBank/getMappedQuestions",
  async ({ entityTypeId, dealCategoryId, orgId, dealId }, { rejectWithValue }) => {
    try {
      let url = "/api/admin/v1/question/mapped";
      const queryParams = [`orgId=${orgId}`];

      if (dealCategoryId) {
        queryParams.push(`dealCategoryId=${dealCategoryId}`);
      }

      if (entityTypeId) {
        queryParams.push(`entityTypeId=${entityTypeId}`);
      }

      if (dealId) {
        queryParams.push(`dealId=${dealId}`);
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }
      const response = await axios.get(url);
      return response.response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addQuestion = createAsyncThunk(
  "questionBank/addQuestion",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`api/admin/v1/question`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const approveRejectAnswer = createAsyncThunk(
  "questionBank/approveRejectAnswer",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `api/admin/questionnaire-thread/answer/review`,
        payload,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const sendQuestionnaire = createAsyncThunk(
  "questionBank/sendQuestionnaire",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `api/admin/questionnaire-thread`,
        payload,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const completeQuestionnaire = createAsyncThunk(
  "questionBank/completeQuestionnaire",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `api/admin/completeQuestionnaire`,
        payload,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateQuestion = createAsyncThunk(
  "questionBank/updateQuestion",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.put(`api/admin/v1/question`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateQuestionnaireThreadStatus = createAsyncThunk(
  "questionBank/updateQuestionnaireThreadStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `api/admin/questionnaire-thread/status`,
        payload,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteQuestion = createAsyncThunk(
  "questionBank/deleteQuestion",
  async (questionId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `api/admin/v1/question/${questionId}`,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getBusinessStatus = createAsyncThunk(
  "questionBank/getBusinessStatus",
  async ({ orgId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/getBusinessStatus/${orgId}`);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const QuestionBankSlice = createSlice({
  name: "questionBank",
  initialState: {
    loading: false,
    dropdownData: {},
    addQuestionLoading: false,
    addQuestionError: null,
    approveRejectAnswerLoading: false,
    approveRejectAnswerError: null,
    sendQuestionnaireLoading: false,
    sendQuestionnaireError: null,
    completeQuestionnaireLoading: false,
    completeQuestionnaireError: null,
    questions: [],
    questionnaireThreads: [],
    idQuestionnaireThread: [],
    filteredQuestions: [],
    mappedQuestions: null,
    updateQuestionLoading: false,
    updateQuestionError: null,
    updateQuestionnaireThreadStatusLoading: false,
    updateQuestionnaireThreadStatusError: null,
    businessStatus: [],
    predefinedThreads: [],
    createPredefinedThreadLoading: false,
    deletePredefinedThreadLoading: false,
    deleteQuestionFromPredefinedThreadLoading: false,
  },

  extraReducers: (builder) => {
    builder.addCase(getQuestionDropdowns.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getQuestionDropdowns.fulfilled, (state, action) => {
      state.loading = false;
      state.dropdownData = action.payload;
    });
    builder.addCase(getQuestionDropdowns.rejected, (state) => {
      state.loading = false;
      state.dropdownData = {};
    });

    builder.addCase(addQuestion.pending, (state) => {
      state.addQuestionLoading = true;
      state.addQuestionError = null;
    });
    builder.addCase(addQuestion.fulfilled, (state, action) => {
      state.addQuestionLoading = false;
      state.addQuestionError = null;
    });
    builder.addCase(addQuestion.rejected, (state, action) => {
      state.addQuestionLoading = false;
      state.addQuestionError = action.payload;
    });
    builder.addCase(approveRejectAnswer.pending, (state) => {
      state.approveRejectAnswerLoading = true;
      state.approveRejectAnswerError = null;
    });
    builder.addCase(approveRejectAnswer.fulfilled, (state, action) => {
      state.approveRejectAnswerLoading = false;
      state.approveRejectAnswerError = null;

      const { answerId, reviewStatus } = action.meta.arg || {};
      if (!answerId || !reviewStatus || !state.idQuestionnaireThread?.questions) {
        return;
      }

      state.idQuestionnaireThread.questions = state.idQuestionnaireThread.questions.map((question) => {
        const matchedAnswer = question.answers?.find((answer) => answer.answerId === answerId);
        if (!matchedAnswer) {
          return question;
        }

        return {
          ...question,
          status: reviewStatus,
          answers: [
            {
              ...matchedAnswer,
              status: reviewStatus,
              answerStatus: reviewStatus,
            },
          ],
        };
      });
    });
    builder.addCase(approveRejectAnswer.rejected, (state, action) => {
      state.approveRejectAnswerLoading = false;
      state.approveRejectAnswerError = action.payload;
    });

    builder.addCase(sendQuestionnaire.pending, (state) => {
      state.sendQuestionnaireLoading = true;
      state.sendQuestionnaireError = null;
    });
    builder.addCase(sendQuestionnaire.fulfilled, (state, action) => {
      state.sendQuestionnaireLoading = false;
      state.sendQuestionnaireError = null;
    });
    builder.addCase(sendQuestionnaire.rejected, (state, action) => {
      state.sendQuestionnaireLoading = false;
      state.sendQuestionnaireError = action.payload;
    });

    builder.addCase(completeQuestionnaire.pending, (state) => {
      state.completeQuestionnaireLoading = true;
      state.completeQuestionnaireError = null;
    });
    builder.addCase(completeQuestionnaire.fulfilled, (state, action) => {
      state.completeQuestionnaireLoading = false;
      state.completeQuestionnaireError = null;
    });
    builder.addCase(completeQuestionnaire.rejected, (state, action) => {
      state.completeQuestionnaireLoading = false;
      state.completeQuestionnaireError = action.payload;
    });

    builder.addCase(getQuestions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getQuestions.fulfilled, (state, action) => {
      state.loading = false;
      state.questions = action.payload;
    });
    builder.addCase(getQuestions.rejected, (state, action) => {
      state.loading = false;
      state.questions = [];
    });

    builder.addCase(getQuestionnaireThreads.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getQuestionnaireThreads.fulfilled, (state, action) => {
      state.loading = false;
      state.questionnaireThreads = action.payload;
    });
    builder.addCase(getQuestionnaireThreads.rejected, (state, action) => {
      state.loading = false;
      state.questionnaireThreads = [];
    });

    builder.addCase(getIdQuestionnaireThread.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getIdQuestionnaireThread.fulfilled, (state, action) => {
      state.loading = false;
      state.idQuestionnaireThread = action.payload;
    });
    builder.addCase(getIdQuestionnaireThread.rejected, (state, action) => {
      state.loading = false;
      state.idQuestionnaireThread = [];
    });

    builder.addCase(getFilteredQuestions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFilteredQuestions.fulfilled, (state, action) => {
      state.loading = false;
      state.filteredQuestions = action.payload;
    });
    builder.addCase(getFilteredQuestions.rejected, (state, action) => {
      state.loading = false;
      state.filteredQuestions = [];
    });

    builder.addCase(getMappedQuestions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getMappedQuestions.fulfilled, (state, action) => {
      state.loading = false;
      state.mappedQuestions = action.payload;
    });
    builder.addCase(getMappedQuestions.rejected, (state) => {
      state.loading = false;
      state.mappedQuestions = null;
    });

    builder.addCase(updateQuestion.pending, (state) => {
      state.updateQuestionLoading = true;
      state.updateQuestionError = null;
    });
    builder.addCase(updateQuestion.fulfilled, (state, action) => {
      state.updateQuestionLoading = false;
      state.updateQuestionError = null;
    });
    builder.addCase(updateQuestion.rejected, (state, action) => {
      state.updateQuestionLoading = false;
      state.updateQuestionError = action.payload;
    });

    builder.addCase(updateQuestionnaireThreadStatus.pending, (state) => {
      state.updateQuestionnaireThreadStatusLoading = true;
      state.updateQuestionnaireThreadStatusError = null;
    });
    builder.addCase(
      updateQuestionnaireThreadStatus.fulfilled,
      (state, action) => {
        state.updateQuestionnaireThreadStatusLoading = false;
        state.updateQuestionnaireThreadStatusError = null;
      },
    );
    builder.addCase(
      updateQuestionnaireThreadStatus.rejected,
      (state, action) => {
        state.updateQuestionnaireThreadStatusLoading = false;
        state.updateQuestionnaireThreadStatusError = action.payload;
      },
    );

    builder.addCase(getBusinessStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBusinessStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.businessStatus = action.payload;
    });
    builder.addCase(getBusinessStatus.rejected, (state, action) => {
      state.loading = false;
      state.businessStatus = [];
    });

    // getPredefinedThreads
    builder.addCase(getPredefinedThreads.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPredefinedThreads.fulfilled, (state, action) => {
      state.loading = false;
      state.predefinedThreads = action.payload;
    });
    builder.addCase(getPredefinedThreads.rejected, (state) => {
      state.loading = false;
      state.predefinedThreads = [];
    });

    // createPredefinedThread
    builder.addCase(createPredefinedThread.pending, (state) => {
      state.createPredefinedThreadLoading = true;
    });
    builder.addCase(createPredefinedThread.fulfilled, (state) => {
      state.createPredefinedThreadLoading = false;
    });
    builder.addCase(createPredefinedThread.rejected, (state) => {
      state.createPredefinedThreadLoading = false;
    });

    // deletePredefinedThread
    builder.addCase(deletePredefinedThread.pending, (state) => {
      state.deletePredefinedThreadLoading = true;
    });
    builder.addCase(deletePredefinedThread.fulfilled, (state) => {
      state.deletePredefinedThreadLoading = false;
    });
    builder.addCase(deletePredefinedThread.rejected, (state) => {
      state.deletePredefinedThreadLoading = false;
    });

    // deleteQuestionFromPredefinedThread
    builder.addCase(deleteQuestionFromPredefinedThread.pending, (state) => {
      state.deleteQuestionFromPredefinedThreadLoading = true;
    });
    builder.addCase(deleteQuestionFromPredefinedThread.fulfilled, (state) => {
      state.deleteQuestionFromPredefinedThreadLoading = false;
    });
    builder.addCase(deleteQuestionFromPredefinedThread.rejected, (state) => {
      state.deleteQuestionFromPredefinedThreadLoading = false;
    });
  },

  reducers: {
    resetState: (state) => {
      state.filteredQuestions = [];
      state.mappedQuestions = null;
    },
  },
});

export const { resetState } = QuestionBankSlice.actions;

export default QuestionBankSlice.reducer;
